import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  collection,
  addDoc,
  getDocs,
  getDoc,
  doc,
  query,
  where,
  orderBy,
  updateDoc,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore'
import { Timestamp } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuth } from '@/contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import {
  TransportTruck,
  TransportTruckStatus,
  ShipmentPlan,
  ShipmentPlanKind,
  ShipmentPlanLocation,
  ShipmentLeg,
  LegFile,
  InstallationStatus,
  InstallationNeedsAssignedCompany,
  InstallationNeedsAssignedAt,
  deriveShipmentLegsFromLocations,
  getLegStatus,
  appendLegStatus,
  getRepairTruckWorkStatus,
  getTruckStatus,
} from '@otw/models/logistics'
import { Vehicle } from '@otw/models/inventory'
import { transportTruckConverter, shipmentPlanConverter, repairTruckConverter } from '@otw/models/converters'
import type {
  RepairTruck,
  RepairTruckWorkStatus,
  RepairTruckWorkNoteMedia,
} from '@otw/models/logistics'

const TRANSPORT_TRUCKS_COLLECTION = 'transport_trucks'
const SHIPMENT_PLANS_COLLECTION = 'shipment_plans'
const REPAIR_TRUCKS_COLLECTION = 'repair_trucks'

export const CUSTOMER_PICKUP_COMPANY_ID = 'customer_pickup'
export const OPTIC_INTERNAL_CLIENT_USER_ID = 'optic'

function parseLogisticsTimestamp(value: unknown): Date | null {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  if (typeof value === 'string') {
    const parsed = new Date(value)
    return Number.isNaN(parsed.getTime()) ? null : parsed
  }
  if (typeof value === 'object' && value !== null) {
    const maybeTs = value as { toDate?: () => Date; toMillis?: () => number }
    if (typeof maybeTs.toDate === 'function') {
      const parsed = maybeTs.toDate()
      return Number.isNaN(parsed.getTime()) ? null : parsed
    }
    if (typeof maybeTs.toMillis === 'function') {
      const parsed = new Date(maybeTs.toMillis())
      return Number.isNaN(parsed.getTime()) ? null : parsed
    }
  }
  return null
}

/** Most recently created shipment plan for a truck (by `created_at`). */
export function getLatestShipmentPlan(plans: ShipmentPlan[]): ShipmentPlan | null {
  if (plans.length === 0) return null
  return [...plans].sort(
    (a, b) => (b.created_at?.toMillis?.() ?? 0) - (a.created_at?.toMillis?.() ?? 0)
  )[0]
}

export function isCustomerPickupOnPlanLastLeg(plan: ShipmentPlan): boolean {
  const legs = plan.shipment_legs ?? []
  if (legs.length === 0) return false
  return legs[legs.length - 1]?.company_id === CUSTOMER_PICKUP_COMPANY_ID
}

function getCustomerPickupAssignedAt(leg: ShipmentLeg): Date | null {
  const history = leg.company_history ?? []
  for (let i = history.length - 1; i >= 0; i -= 1) {
    const entry = history[i]
    if (entry.company_id !== CUSTOMER_PICKUP_COMPANY_ID) continue
    const assignedAt = parseLogisticsTimestamp(entry.timestamp)
    if (assignedAt) return assignedAt
  }
  return null
}

/**
 * When the truck is not yet `delivered`, treat customer pickup on the final leg of the
 * latest shipment plan as delivery-complete for SLA/timeline purposes.
 */
export function getCustomerPickupSyntheticDeliveryAt(
  truck: TransportTruck,
  plans: ShipmentPlan[]
): Date | null {
  if (getTruckStatus(truck) === 'delivered') return null

  const latestPlan = getLatestShipmentPlan(plans)
  if (!latestPlan || !isCustomerPickupOnPlanLastLeg(latestPlan)) return null

  const lastLeg = latestPlan.shipment_legs![latestPlan.shipment_legs!.length - 1]
  return (
    getCustomerPickupAssignedAt(lastLeg) ??
    parseLogisticsTimestamp(latestPlan.updated_at) ??
    parseLogisticsTimestamp(latestPlan.created_at)
  )
}

export function hasShipmentPlanClientSelected(plan: ShipmentPlan | null | undefined): boolean {
  const clientId = plan?.client_user_id
  return Boolean(clientId && clientId !== OPTIC_INTERNAL_CLIENT_USER_ID)
}

function getTruckPickedUpFromRepairAt(
  truck: TransportTruck,
  repairShopAt: Date | null
): Date | null {
  const events = (truck.status_history ?? [])
    .map((entry) => ({
      status: entry.status as string,
      enteredAt: parseLogisticsTimestamp(entry.entered_at),
    }))
    .filter((entry): entry is { status: string; enteredAt: Date } => entry.enteredAt !== null)
    .sort((a, b) => a.enteredAt.getTime() - b.enteredAt.getTime())

  const repairAt = repairShopAt ?? events.find((event) => event.status === 'repair')?.enteredAt ?? null
  const pickup = events.find(
    (event) =>
      event.status === 'in_transit' &&
      (!repairAt || event.enteredAt.getTime() >= repairAt.getTime())
  )
  return pickup?.enteredAt ?? null
}

/**
 * Post-repair delivery SLA starts only after pickup from repair and only when the
 * truck's latest shipment plan has a real customer assigned (not Optic/internal).
 */
export function getPostRepairDeliveryStartAt(
  trucks: TransportTruck[],
  plans: ShipmentPlan[],
  repairShopAt: Date | null
): Date | null {
  const plansByTruckId = groupShipmentPlansByTruckId(plans)
  const pickupDates: Date[] = []

  for (const truck of trucks) {
    const latestPlan = getLatestShipmentPlan(plansByTruckId.get(truck.id) ?? [])
    if (!hasShipmentPlanClientSelected(latestPlan)) continue

    const pickedUpAt = getTruckPickedUpFromRepairAt(truck, repairShopAt)
    if (pickedUpAt) pickupDates.push(pickedUpAt)
  }

  if (pickupDates.length === 0) return null
  return new Date(Math.min(...pickupDates.map((date) => date.getTime())))
}

/** Group shipment plans by `transport_truck_id`. */
export function groupShipmentPlansByTruckId(plans: ShipmentPlan[]): Map<string, ShipmentPlan[]> {
  const map = new Map<string, ShipmentPlan[]>()
  for (const plan of plans) {
    const truckId = plan.transport_truck_id
    const existing = map.get(truckId)
    if (existing) {
      existing.push(plan)
    } else {
      map.set(truckId, [plan])
    }
  }
  return map
}

export interface TransportTruckWithVehicle {
  truck: TransportTruck
  vehicle: Vehicle | null
  shipmentPlans: ShipmentPlan[]
}

export interface RepairShopTruckCounts {
  atShop: number
  enRoute: number
}

/** Trucks at a repair shop plus trucks heading there on a non-completed leg. */
export function buildRepairShopTruckCountsById(
  transportTrucksWithVehicles: TransportTruckWithVehicle[]
): Record<string, RepairShopTruckCounts> {
  const atShop: Record<string, number> = {}
  const enRouteTruckIdsByShop: Record<string, Set<string>> = {}

  for (const { truck, shipmentPlans } of transportTrucksWithVehicles) {
    if (getTruckStatus(truck) === 'delivered') continue

    const truckStatus = getTruckStatus(truck)
    const locationId = truck.currentLocationId
    const atShopLocationId = truckStatus === 'repair' ? locationId : undefined

    if (locationId && truckStatus === 'repair') {
      atShop[locationId] = (atShop[locationId] ?? 0) + 1
    }

    for (const plan of shipmentPlans) {
      for (const leg of plan.shipment_legs ?? []) {
        if (leg.to_type !== 'repair' || !leg.to_id) continue
        if (getLegStatus(leg) === 'Completed') continue
        if (atShopLocationId === leg.to_id) continue

        if (!enRouteTruckIdsByShop[leg.to_id]) {
          enRouteTruckIdsByShop[leg.to_id] = new Set()
        }
        enRouteTruckIdsByShop[leg.to_id].add(truck.id)
      }
    }
  }

  const shopIds = new Set([...Object.keys(atShop), ...Object.keys(enRouteTruckIdsByShop)])
  const result: Record<string, RepairShopTruckCounts> = {}
  for (const shopId of shopIds) {
    result[shopId] = {
      atShop: atShop[shopId] ?? 0,
      enRoute: enRouteTruckIdsByShop[shopId]?.size ?? 0,
    }
  }
  return result
}

export function formatRepairShopCapacityLabel(
  shop: { capacity?: number | null },
  counts: RepairShopTruckCounts | undefined,
  numberFormatter: Intl.NumberFormat
): string {
  const atShop = counts?.atShop ?? 0
  const enRoute = counts?.enRoute ?? 0
  const total = atShop + enRoute
  const formattedTotal = numberFormatter.format(total)
  const capacityValue =
    typeof shop.capacity === 'number' && Number.isFinite(shop.capacity) && shop.capacity >= 0
      ? numberFormatter.format(shop.capacity)
      : '—'
  const base = `${formattedTotal}/${capacityValue}`
  if (enRoute > 0) {
    return `${base} (${numberFormatter.format(enRoute)} on the way)`
  }
  return base
}

export interface CreateShipmentPlanParams {
  transportTruckId: string
  clientUserId?: string
  description?: string
  plan_kind?: ShipmentPlanKind
  locations?: ShipmentPlanLocation[]
  shipment_legs?: ShipmentLeg[]
}

export const useLogistics = () => {
  const queryClient = useQueryClient()
  const { userInfo } = useAuth()
  const { showSuccess, showError } = useSnackbar()

  const transportTrucksCollection = collection(db, TRANSPORT_TRUCKS_COLLECTION).withConverter(
    transportTruckConverter
  )
  const shipmentPlansCollection = collection(db, SHIPMENT_PLANS_COLLECTION).withConverter(
    shipmentPlanConverter
  )
  const repairTrucksCollection = collection(db, REPAIR_TRUCKS_COLLECTION).withConverter(
    repairTruckConverter
  )

  const {
    data: transportTrucks = [],
    isLoading: isLoadingTransportTrucks,
  } = useQuery({
    queryKey: ['transport_trucks'],
    queryFn: async () => {
      const q = query(transportTrucksCollection, orderBy('created_at', 'desc'))
      const snapshot = await getDocs(q)
      return snapshot.docs.map((d) => d.data())
    },
  })

  const { data: shipmentPlans = [] } = useQuery({
    queryKey: ['shipment_plans'],
    queryFn: async () => {
      const q = query(shipmentPlansCollection, orderBy('created_at', 'desc'))
      const snapshot = await getDocs(q)
      return snapshot.docs.map((d) => d.data())
    },
  })

  const { data: repairTrucks = [], isLoading: isLoadingRepairTrucks } = useQuery({
    queryKey: ['repair_trucks', 'all'],
    queryFn: async () => {
      const snapshot = await getDocs(repairTrucksCollection)
      return snapshot.docs.map((d) => d.data())
    },
  })

  const vehicleIds = useMemo(
    () => [...new Set(transportTrucks.map((t) => t.vehicle_id))],
    [transportTrucks]
  )
  const vehicleIdsKey = useMemo(
    () => [...vehicleIds].sort().join(','),
    [vehicleIds]
  )

  const { data: vehiclesMap = new Map<string, Vehicle | null>(), isLoading: isLoadingWithVehicles } = useQuery({
    queryKey: ['transport_truck_vehicles', vehicleIdsKey],
    queryFn: async () => {
      const vehiclePromises = vehicleIds.map(async (vehicleId) => {
        try {
          const vehicleDoc = await getDoc(doc(db, 'master_inventory', vehicleId))
          return vehicleDoc.exists()
            ? ({ id: vehicleDoc.id, ...vehicleDoc.data() } as Vehicle)
            : null
        } catch {
          return null
        }
      })
      const vehicles = await Promise.all(vehiclePromises)
      const vehiclesMap = new Map<string, Vehicle | null>()
      vehicleIds.forEach((id, i) => vehiclesMap.set(id, vehicles[i]))
      return vehiclesMap
    },
    enabled: vehicleIds.length > 0,
  })

  const transportTrucksWithVehicles = useMemo(() => {
    const plansByTruck = new Map<string, ShipmentPlan[]>()
    for (const plan of shipmentPlans) {
      const truckId = plan.transport_truck_id
      if (!plansByTruck.has(truckId)) plansByTruck.set(truckId, [])
      plansByTruck.get(truckId)!.push(plan)
    }

    return transportTrucks.map((truck) => ({
      truck,
      vehicle: vehiclesMap.get(truck.vehicle_id) ?? null,
      shipmentPlans: plansByTruck.get(truck.id) ?? [],
    })) as TransportTruckWithVehicle[]
  }, [transportTrucks, shipmentPlans, vehiclesMap])

  const repairShopTruckCountsById = useMemo(
    () => buildRepairShopTruckCountsById(transportTrucksWithVehicles),
    [transportTrucksWithVehicles]
  )

  const repairShopAvgDropoffToCompleteMsById = useMemo(() => {
    const totalsByShopId = new Map<string, { totalMs: number; count: number }>()
    for (const repairTruck of repairTrucks) {
      const shopId = repairTruck.repair_shop_location_id
      const dropoffMs = repairTruck.created_at?.toMillis?.()
      const completedEntry = [...(repairTruck.work_status_history ?? [])]
        .reverse()
        .find((entry) => entry.status?.toLowerCase() === 'complete')
      const completedMs = completedEntry?.timestamp?.toMillis?.()
      if (!shopId || !dropoffMs || !completedMs || completedMs < dropoffMs) continue
      const existing = totalsByShopId.get(shopId) ?? { totalMs: 0, count: 0 }
      totalsByShopId.set(shopId, {
        totalMs: existing.totalMs + (completedMs - dropoffMs),
        count: existing.count + 1,
      })
    }

    return Array.from(totalsByShopId.entries()).reduce<Record<string, number>>((acc, [shopId, aggregate]) => {
      if (aggregate.count > 0) {
        acc[shopId] = aggregate.totalMs / aggregate.count
      }
      return acc
    }, {})
  }, [repairTrucks])

  const transporterGroupAvgReadyToInProgressMsById = useMemo(() => {
    const totalsByCompanyId = new Map<string, { totalMs: number; count: number }>()
    for (const plan of shipmentPlans) {
      for (const leg of plan.shipment_legs ?? []) {
        const companyId = leg.company_id
        if (!companyId) continue

        const readyDateValue = leg.ready_date
        if (!readyDateValue) continue
        const readyDate = new Date(`${readyDateValue}T00:00:00`)
        const readyMs = readyDate.getTime()
        if (!Number.isFinite(readyMs)) continue

        const inProgressEntry = (leg.status_history ?? []).find(
          (entry) => entry.status?.toLowerCase() === 'in progress'
        )
        const inProgressMs = inProgressEntry?.updated_at?.toMillis?.()
        if (!inProgressMs || inProgressMs < readyMs) continue

        const existing = totalsByCompanyId.get(companyId) ?? { totalMs: 0, count: 0 }
        totalsByCompanyId.set(companyId, {
          totalMs: existing.totalMs + (inProgressMs - readyMs),
          count: existing.count + 1,
        })
      }
    }

    return Array.from(totalsByCompanyId.entries()).reduce<Record<string, number>>((acc, [companyId, aggregate]) => {
      if (aggregate.count > 0) {
        acc[companyId] = aggregate.totalMs / aggregate.count
      }
      return acc
    }, {})
  }, [shipmentPlans])

  const addTransportTruckMutation = useMutation({
    mutationFn: async (params: { vehicleId: string; vin: string }) => {
      const now = Timestamp.now()
      const newTruck: Omit<TransportTruck, 'id'> = {
        vehicle_id: params.vehicleId,
        vin: params.vin,
        status_history: [{ status: 'draft', entered_at: now }],
        created_at: now,
        created_by: userInfo?.id ?? '',
        updated_at: now,
      }
      const docRef = await addDoc(transportTrucksCollection, {
        ...newTruck,
        id: '', // Omitted in toFirestore output
      } as TransportTruck)
      return { id: docRef.id, ...newTruck } as TransportTruck
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transport_trucks'] })
      showSuccess('Truck added to transport fleet')
    },
    onError: (error: Error) => {
      showError(`Failed to add truck: ${error.message}`)
    },
  })

  const createShipmentPlanMutation = useMutation<ShipmentPlan, Error, CreateShipmentPlanParams>({
    mutationFn: async (params) => {
      const now = Timestamp.now()
      const locations = params.locations ?? []
      const shipment_legs = params.shipment_legs ?? deriveShipmentLegsFromLocations(locations)
      const newPlan: Omit<ShipmentPlan, 'id'> = {
        transport_truck_id: params.transportTruckId,
        client_user_id: params.clientUserId,
        description: params.description,
        plan_kind: params.plan_kind === 'post_delivery_repair' ? 'post_delivery_repair' : 'standard',
        locations,
        shipment_legs,
        created_at: now,
        created_by: userInfo?.id ?? '',
        updated_at: now,
      }
      const docRef = await addDoc(shipmentPlansCollection, {
        ...newPlan,
        id: '',
      } as ShipmentPlan)
      return { id: docRef.id, ...newPlan } as ShipmentPlan
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipment_plans'] })
      showSuccess('Shipment plan created')
    },
    onError: (error: Error) => {
      showError(`Failed to create shipment plan: ${error.message}`)
    },
  })

  const updateShipmentPlanMutation = useMutation({
    mutationFn: async (params: {
      planId: string
      client_user_id: string | null
      description: string | null
      locations: ShipmentPlanLocation[]
      shipment_legs: ShipmentLeg[]
    }) => {
      const docRef = doc(db, SHIPMENT_PLANS_COLLECTION, params.planId)
      const shipment_legs = params.shipment_legs.map((leg) => ({
        from_id: leg.from_id ?? '',
        from_type: leg.from_type ?? 'pickup',
        to_id: leg.to_id ?? '',
        to_type: leg.to_type ?? 'pickup',
        company_id: leg.company_id ?? null,
        company_history: (leg.company_history ?? []).map((h) => ({
          company_id: h.company_id ?? '',
          timestamp: h.timestamp ?? Timestamp.now(),
        })),
        driver_id: leg.driver_id ?? null,
        assignment_notes: leg.assignment_notes ?? null,
        assignment_notes_photo: leg.assignment_notes_photo
          ? {
              fileName: leg.assignment_notes_photo.fileName ?? '',
              storagePath: leg.assignment_notes_photo.storagePath ?? '',
              url: leg.assignment_notes_photo.url ?? '',
              fileSize: leg.assignment_notes_photo.fileSize ?? 0,
              fileType: leg.assignment_notes_photo.fileType ?? '',
              uploadedAt: leg.assignment_notes_photo.uploadedAt ?? Timestamp.now(),
              uploadedBy: leg.assignment_notes_photo.uploadedBy ?? '',
            }
          : null,
        dispatch_info: leg.dispatch_info ?? null,
        estimated_shipping_price: leg.estimated_shipping_price ?? null,
        scheduled_pickup_date: leg.scheduled_pickup_date ?? null,
        ready_date: leg.ready_date ?? null,
        ready: leg.ready ?? null,
        rejected: leg.rejected ?? false,
        admin_completion_requested: leg.admin_completion_requested ?? false,
        charge_cancelled: leg.charge_cancelled ?? false,
        charge_cancelled_note: leg.charge_cancelled_note ?? null,
        charge_cancelled_at: leg.charge_cancelled_at ?? null,
        charge_cancelled_by: leg.charge_cancelled_by ?? null,
        status_history: (leg.status_history ?? []).map((h) => ({
          status: h.status ?? 'Draft',
          updated_at: h.updated_at ?? Timestamp.now(),
          notes: h.notes ?? null,
        })),
        gate_passes: (leg.gate_passes ?? []).map((f: LegFile) => ({
          fileName: f.fileName ?? '',
          storagePath: f.storagePath ?? '',
          url: f.url ?? '',
          fileSize: f.fileSize ?? 0,
          fileType: f.fileType ?? '',
          uploadedAt: f.uploadedAt ?? Timestamp.now(),
          uploadedBy: f.uploadedBy ?? '',
        })),
      }))
      const locations = params.locations.map((loc) => ({
        location_id: loc.location_id ?? '',
        location_type: loc.location_type ?? 'pickup',
        notes: loc.notes ?? null,
      }))
      const updateData = {
        updated_at: Timestamp.now(),
        client_user_id: params.client_user_id ?? null,
        description: params.description ?? null,
        locations,
        shipment_legs,
      }
      await updateDoc(docRef, updateData)
      return params.planId
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: ['shipment_plans'] })
      const previousPlans = queryClient.getQueryData<ShipmentPlan[]>(['shipment_plans'])

      queryClient.setQueryData<ShipmentPlan[]>(['shipment_plans'], (current = []) =>
        current.map((plan) =>
          plan.id === params.planId
            ? {
                ...plan,
                client_user_id: params.client_user_id ?? undefined,
                description: params.description ?? undefined,
                locations: params.locations,
                shipment_legs: params.shipment_legs,
                updated_at: Timestamp.now(),
              }
            : plan
        )
      )

      return { previousPlans }
    },
    onSuccess: () => {
      showSuccess('Shipment plan updated')
    },
    onError: (error: Error, _params, context) => {
      if (context?.previousPlans) {
        queryClient.setQueryData(['shipment_plans'], context.previousPlans)
      }
      showError(`Failed to update shipment plan: ${error.message}`)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['shipment_plans'], refetchType: 'inactive' })
    },
  })

  const approveLegPhotosMutation = useMutation({
    mutationFn: async (params: { plan: ShipmentPlan; legIndex: number }) => {
      const legs = [...(params.plan.shipment_legs ?? [])]
      const leg = legs[params.legIndex]
      if (!leg) throw new Error('Leg not found')
      const approvedLeg = appendLegStatus(
        leg,
        getLegStatus(leg),
        Timestamp.now(),
        'Pre-delivery photos approved'
      )
      legs[params.legIndex] = approvedLeg
      await updateShipmentPlanMutation.mutateAsync({
        planId: params.plan.id,
        client_user_id: params.plan.client_user_id ?? null,
        description: params.plan.description ?? null,
        locations: params.plan.locations ?? [],
        shipment_legs: legs,
      })
    },
  })

  const rejectLegPhotosMutation = useMutation({
    mutationFn: async (params: { plan: ShipmentPlan; legIndex: number }) => {
      const legs = [...(params.plan.shipment_legs ?? [])]
      const leg = legs[params.legIndex]
      if (!leg) throw new Error('Leg not found')
      const rejectedLeg = appendLegStatus(
        leg,
        getLegStatus(leg),
        Timestamp.now(),
        'Pre-delivery photos rejected - needs repair'
      )
      legs[params.legIndex] = rejectedLeg
      await updateShipmentPlanMutation.mutateAsync({
        planId: params.plan.id,
        client_user_id: params.plan.client_user_id ?? null,
        description: params.plan.description ?? null,
        locations: params.plan.locations ?? [],
        shipment_legs: legs,
      })
      return { ...params.plan, shipment_legs: legs } as ShipmentPlan
    },
  })

  const updateTransportTruckMutation = useMutation({
    mutationFn: async (params: {
      truckId: string
      status: TransportTruckStatus
      currentLocationId: string | null
    }) => {
      const docRef = doc(db, TRANSPORT_TRUCKS_COLLECTION, params.truckId)
      const truckSnap = await getDoc(docRef)
      const truckData = truckSnap.data()
      const existing = Array.isArray(truckData?.status_history)
        ? truckData.status_history
        : []
      const previousStatus = existing.length
        ? (existing[existing.length - 1]?.status as TransportTruckStatus | undefined)
        : undefined
      const previousLocationId =
        typeof truckData?.currentLocationId === 'string' ? truckData.currentLocationId : null
      const vehicleId = typeof truckData?.vehicle_id === 'string' ? truckData.vehicle_id : ''
      const now = Timestamp.now()
      const status_history = [...existing, { status: params.status, entered_at: now }]
      const updateData = {
        updated_at: now,
        currentLocationId: params.currentLocationId,
        status_history,
      }
      await updateDoc(docRef, updateData)

      // A repaired truck can be set to `awaiting_transport` while it is still at the
      // repair shop, then `delivered` when the outbound leg completes. Treat leaving
      // the repair location as the reset point, not only `repair -> non-repair`.
      const isLeavingPreviousLocation = previousLocationId !== params.currentLocationId
      const isLeavingRepair = previousLocationId && isLeavingPreviousLocation
      if (isLeavingRepair && previousLocationId && vehicleId) {
        const matchingRepairSnap = await getDocs(
          query(
            repairTrucksCollection,
            where('transport_truck_id', '==', params.truckId),
            where('repair_shop_location_id', '==', previousLocationId)
          )
        )
        const matchingRepair = matchingRepairSnap.docs[0]?.data()

        if (matchingRepair?.being_painted) {
          const vehicleRef = doc(db, 'master_inventory', vehicleId)
          const vehicleSnap = await getDoc(vehicleRef)
          if (vehicleSnap.exists()) {
            const vehicleData = vehicleSnap.data()
            const previousColor = vehicleData?.color
            const userId = userInfo?.id ?? ''
            const colorHistory = Array.isArray(vehicleData?.color_history)
              ? [...vehicleData.color_history]
              : []
            if (previousColor !== 'White') {
              colorHistory.push({
                color: 'White',
                changed_at: now.toDate().toISOString(),
                ...(userId && { changed_by_user_id: userId }),
              })
            }

            await updateDoc(vehicleRef, {
              color: 'White',
              being_painted_date: null,
              color_history: colorHistory,
              updated_at: now.toDate().toISOString(),
              updated_by: userId || null,
            })
          }
        }
      }

      if (params.status === 'repair' && params.currentLocationId) {
        const newRepair: Omit<RepairTruck, 'id'> = {
          transport_truck_id: params.truckId,
          repair_shop_location_id: params.currentLocationId,
          priority_rank: null,
          work_notes: [],
          created_at: now,
          work_status_history: [
            {
              status: 'Awaiting estimate',
              timestamp: now,
              user_id: userInfo?.id ?? '',
            },
          ],
          flagged: false,
          paid: false,
          paid_date: null,
          being_painted: false,
          being_painted_date: null,
          // New arrivals after intake launch must complete intake first.
          requires_intake: true,
        }
        await addDoc(repairTrucksCollection, newRepair as RepairTruck)
      }

      return params.truckId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transport_trucks'] })
      queryClient.invalidateQueries({ queryKey: ['transport_truck_vehicles'] })
      showSuccess('Truck status updated')
    },
    onError: (error: Error) => {
      showError(`Failed to update truck status: ${error.message}`)
    },
  })

  const revertLatestTransportTruckStatusMutation = useMutation({
    mutationFn: async (params: { truckId: string }) => {
      const docRef = doc(db, TRANSPORT_TRUCKS_COLLECTION, params.truckId)
      const truckSnap = await getDoc(docRef)
      if (!truckSnap.exists()) {
        throw new Error('Transport truck not found')
      }

      const truckData = truckSnap.data()
      const existing = Array.isArray(truckData?.status_history)
        ? [...truckData.status_history]
        : []

      if (existing.length === 0) {
        return params.truckId
      }

      existing.pop()
      await updateDoc(docRef, {
        status_history: existing,
        updated_at: Timestamp.now(),
      })

      return params.truckId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transport_trucks'] })
      queryClient.invalidateQueries({ queryKey: ['transport_truck_vehicles'] })
      showSuccess('Latest truck status removed')
    },
    onError: (error: Error) => {
      showError(`Failed to remove latest truck status: ${error.message}`)
    },
  })

  const deleteShipmentPlanMutation = useMutation({
    mutationFn: async (planId: string) => {
      const docRef = doc(db, SHIPMENT_PLANS_COLLECTION, planId)
      await deleteDoc(docRef)
      return planId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipment_plans'] })
      showSuccess('Shipment plan deleted')
    },
    onError: (error: Error) => {
      showError(`Failed to delete shipment plan: ${error.message}`)
    },
  })

  const deleteTransportTruckMutation = useMutation({
    mutationFn: async (truckId: string) => {
      const plansForTruck = shipmentPlans.filter((p) => p.transport_truck_id === truckId)
      const batch = writeBatch(db)
      for (const plan of plansForTruck) {
        batch.delete(doc(db, SHIPMENT_PLANS_COLLECTION, plan.id))
      }
      batch.delete(doc(db, TRANSPORT_TRUCKS_COLLECTION, truckId))
      await batch.commit()
      return truckId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['transport_trucks'] })
      queryClient.invalidateQueries({ queryKey: ['shipment_plans'] })
      showSuccess('Transport truck deleted')
    },
    onError: (error: Error) => {
      showError(`Failed to delete transport truck: ${error.message}`)
    },
  })

  const updateTransportTruckNeedsMutation = useMutation({
    mutationFn: async (params: {
      truckId: string
      installation_status: InstallationStatus
      installation_needs_assigned_company?: InstallationNeedsAssignedCompany
      installation_needs_assigned_at?: InstallationNeedsAssignedAt
      installation_needs_hidden?: boolean
      installation_needs_hidden_at?: Timestamp | null
      installation_needs_hidden_by?: string | null
      installation_needs_force_completed?: boolean
      installation_needs_force_completed_at?: Timestamp | null
      installation_needs_force_completed_by?: string | null
      successMessage?: string
    }) => {
      const docRef = doc(db, TRANSPORT_TRUCKS_COLLECTION, params.truckId)
      const updateData: {
        updated_at: Timestamp
        installation_status: InstallationStatus
        installation_needs_assigned_company?: InstallationNeedsAssignedCompany
        installation_needs_assigned_at?: InstallationNeedsAssignedAt
        installation_needs_hidden?: boolean
        installation_needs_hidden_at?: Timestamp | null
        installation_needs_hidden_by?: string | null
        installation_needs_force_completed?: boolean
        installation_needs_force_completed_at?: Timestamp | null
        installation_needs_force_completed_by?: string | null
      } = {
        updated_at: Timestamp.now(),
        installation_status: params.installation_status,
      }
      if (params.installation_needs_assigned_company !== undefined) {
        const assigned = params.installation_needs_assigned_company
        const cleaned: InstallationNeedsAssignedCompany = {}
        if (assigned.anti_theft) cleaned.anti_theft = assigned.anti_theft
        if (assigned.cameras) cleaned.cameras = assigned.cameras
        if (assigned.backup_sensors) cleaned.backup_sensors = assigned.backup_sensors
        updateData.installation_needs_assigned_company = cleaned
      }
      if (params.installation_needs_assigned_at !== undefined) {
        updateData.installation_needs_assigned_at = params.installation_needs_assigned_at
      }
      if (params.installation_needs_hidden !== undefined) {
        updateData.installation_needs_hidden = params.installation_needs_hidden
        updateData.installation_needs_hidden_at =
          params.installation_needs_hidden_at !== undefined
            ? params.installation_needs_hidden_at
            : params.installation_needs_hidden
              ? Timestamp.now()
              : null
        updateData.installation_needs_hidden_by =
          params.installation_needs_hidden_by !== undefined
            ? params.installation_needs_hidden_by
            : params.installation_needs_hidden
              ? (userInfo?.id ?? null)
              : null
      }
      if (params.installation_needs_force_completed !== undefined) {
        updateData.installation_needs_force_completed = params.installation_needs_force_completed
        updateData.installation_needs_force_completed_at =
          params.installation_needs_force_completed_at !== undefined
            ? params.installation_needs_force_completed_at
            : params.installation_needs_force_completed
              ? Timestamp.now()
              : null
        updateData.installation_needs_force_completed_by =
          params.installation_needs_force_completed_by !== undefined
            ? params.installation_needs_force_completed_by
            : params.installation_needs_force_completed
              ? (userInfo?.id ?? null)
              : null
      }
      await updateDoc(docRef, updateData)
      return { truckId: params.truckId, successMessage: params.successMessage }
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['transport_trucks'] })
      showSuccess(result.successMessage || 'Vehicle needs updated')
    },
    onError: (error: Error) => {
      showError(`Failed to update vehicle needs: ${error.message}`)
    },
  })

  const addRepairTruckNoteMutation = useMutation({
    mutationFn: async (params: {
      repairTruckId: string
      note: string
      visible_to_repair_users?: boolean
      /** Optional media (photo/video/invoice/estimate/damage_photo) — same shape as repair uploader. */
      media?: RepairTruckWorkNoteMedia
      /** When true, skips the per-call success toast (e.g. bulk add from UI). */
      suppressSuccessToast?: boolean
    }) => {
      const trimmedNote = params.note.trim()
      if (!trimmedNote && !params.media?.storage_path) {
        throw new Error('Provide a note or media attachment')
      }
      const docRef = doc(db, REPAIR_TRUCKS_COLLECTION, params.repairTruckId)
      const snap = await getDoc(docRef)
      if (!snap.exists()) throw new Error('Repair truck not found')
      const data = snap.data()
      const existingNotes = Array.isArray(data?.work_notes) ? data.work_notes : []
      const now = Timestamp.now()
      const userName = [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(' ').trim()
      const entry: Record<string, unknown> = {
        note: trimmedNote,
        timestamp: now,
        user_id: userInfo?.id ?? '',
        user_name: userName || userInfo?.email || undefined,
        visible_to_repair_users: params.visible_to_repair_users === true,
      }
      if (params.media?.storage_path) {
        entry.media = {
          type: params.media.type,
          storage_path: params.media.storage_path,
        }
      }
      const work_notes = [...existingNotes, entry]
      await updateDoc(docRef, { work_notes })
      return params
    },
    onSuccess: (_repairTruckId, variables) => {
      queryClient.invalidateQueries({ queryKey: ['repair_trucks'] })
      if (!variables.suppressSuccessToast) {
        showSuccess(variables.media ? 'Media added' : 'Note added')
      }
    },
    onError: (error: Error) => {
      showError(`Failed to add note: ${error.message}`)
    },
  })

  const updateRepairTruckNoteMutation = useMutation({
    mutationFn: async (params: {
      repairTruckId: string
      noteIndex: number
      note?: string
      visible_to_repair_users?: boolean
    }) => {
      if (params.note === undefined && params.visible_to_repair_users === undefined) {
        throw new Error('Nothing to update')
      }
      const docRef = doc(db, REPAIR_TRUCKS_COLLECTION, params.repairTruckId)
      const snap = await getDoc(docRef)
      if (!snap.exists()) throw new Error('Repair truck not found')
      const data = snap.data()
      const existingNotes = Array.isArray(data?.work_notes) ? [...data.work_notes] : []
      if (params.noteIndex < 0 || params.noteIndex >= existingNotes.length) {
        throw new Error('Note not found')
      }
      const prev = existingNotes[params.noteIndex] as Record<string, unknown>
      const next: Record<string, unknown> = { ...prev }
      if (typeof params.note === 'string') {
        next.note = params.note.trim()
      }
      if (typeof params.visible_to_repair_users === 'boolean') {
        next.visible_to_repair_users = params.visible_to_repair_users
      }
      existingNotes[params.noteIndex] = next
      await updateDoc(docRef, { work_notes: existingNotes })
      return params.repairTruckId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair_trucks'] })
      showSuccess('Note updated')
    },
    onError: (error: Error) => {
      showError(`Failed to update note: ${error.message}`)
    },
  })

  const deleteRepairTruckNoteMutation = useMutation({
    mutationFn: async (params: { repairTruckId: string; noteIndex: number }) => {
      const docRef = doc(db, REPAIR_TRUCKS_COLLECTION, params.repairTruckId)
      const snap = await getDoc(docRef)
      if (!snap.exists()) throw new Error('Repair truck not found')
      const data = snap.data()
      const existingNotes = Array.isArray(data?.work_notes) ? [...data.work_notes] : []
      if (params.noteIndex < 0 || params.noteIndex >= existingNotes.length) {
        throw new Error('Note not found')
      }
      existingNotes.splice(params.noteIndex, 1)
      await updateDoc(docRef, { work_notes: existingNotes })
      return params.repairTruckId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair_trucks'] })
      showSuccess('Note deleted')
    },
    onError: (error: Error) => {
      showError(`Failed to delete note: ${error.message}`)
    },
  })

  const updateRepairTruckWorkStatusMutation = useMutation({
    mutationFn: async (params: {
      repairTruckId: string
      work_status: RepairTruckWorkStatus
    }) => {
      const docRef = doc(db, REPAIR_TRUCKS_COLLECTION, params.repairTruckId)
      const snap = await getDoc(docRef)
      if (!snap.exists()) throw new Error('Repair truck not found')
      const data = snap.data()
      const now = Timestamp.now()
      const userId = userInfo?.id ?? ''

      const existingStatusHistory = Array.isArray(data?.work_status_history)
        ? data.work_status_history
        : []
      const currentStatus = getRepairTruckWorkStatus({
        work_status_history: existingStatusHistory as RepairTruck['work_status_history'],
      })
      if (currentStatus === params.work_status) return params.repairTruckId

      const work_status_history = [
        ...existingStatusHistory,
        { status: params.work_status, timestamp: now, user_id: userId },
      ]

      const existingNotes = Array.isArray(data?.work_notes) ? data.work_notes : []
      const work_notes = [
        ...existingNotes,
        {
          note: `Work status updated to ${params.work_status}`,
          timestamp: now,
          user_id: userId,
        },
      ]

      await updateDoc(docRef, { work_status_history, work_notes })
      return params.repairTruckId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair_trucks'] })
      showSuccess('Work status updated')
    },
    onError: (error: Error) => {
      showError(`Failed to update work status: ${error.message}`)
    },
  })

  const reviewRepairTruckEstimateMutation = useMutation({
    mutationFn: async (params: {
      repairTruckId: string
      noteIndex: number
      status: 'approved' | 'rejected'
      note?: string
    }) => {
      const docRef = doc(db, REPAIR_TRUCKS_COLLECTION, params.repairTruckId)
      const snap = await getDoc(docRef)
      if (!snap.exists()) throw new Error('Repair truck not found')
      const data = snap.data()
      const existingNotes = Array.isArray(data?.work_notes) ? [...data.work_notes] : []
      if (params.noteIndex < 0 || params.noteIndex >= existingNotes.length) {
        throw new Error('Estimate note not found')
      }
      const prev = existingNotes[params.noteIndex] as Record<string, unknown>
      const media = prev.media as { type?: string; storage_path?: string } | undefined
      if (media?.type !== 'estimate' || !media.storage_path) {
        throw new Error('Selected note is not an estimate PDF')
      }
      const now = Timestamp.now()
      const userId = userInfo?.id ?? ''
      const reviewNote = params.note?.trim()
      existingNotes[params.noteIndex] = {
        ...prev,
        estimate_review: {
          status: params.status,
          decided_at: now,
          decided_by: userId,
          ...(reviewNote ? { note: reviewNote } : {}),
        },
      }

      if (params.status === 'approved') {
        const existingStatusHistory = Array.isArray(data?.work_status_history)
          ? data.work_status_history
          : []
        const currentStatus = getRepairTruckWorkStatus({
          work_status_history: existingStatusHistory as RepairTruck['work_status_history'],
        })
        if (currentStatus === 'Awaiting estimate') {
          const work_status_history = [
            ...existingStatusHistory,
            { status: 'in progress' as const, timestamp: now, user_id: userId },
          ]
          const work_notes = [
            ...existingNotes,
            {
              note: 'Work status updated to in progress',
              timestamp: now,
              user_id: userId,
            },
          ]
          await updateDoc(docRef, { work_notes, work_status_history })
          return params
        }
      }

      await updateDoc(docRef, { work_notes: existingNotes })
      return params
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['repair_trucks'] })
      queryClient.invalidateQueries({ queryKey: ['repair_trucks_by_ids'] })
      showSuccess(
        variables.status === 'approved' ? 'Estimate approved' : 'Estimate rejected',
      )
    },
    onError: (error: Error) => {
      showError(`Failed to review estimate: ${error.message}`)
    },
  })

  const updateRepairTruckPaidMutation = useMutation({
    mutationFn: async (params: { repairTruckId: string; paid: boolean }) => {
      const docRef = doc(db, REPAIR_TRUCKS_COLLECTION, params.repairTruckId)
      const snap = await getDoc(docRef)
      if (!snap.exists()) throw new Error('Repair truck not found')
      const data = snap.data()
      const currentPaid = Boolean(data?.paid)
      if (currentPaid === params.paid) return params.repairTruckId

      const now = Timestamp.now()
      const userId = userInfo?.id ?? ''
      const existingNotes = Array.isArray(data?.work_notes) ? data.work_notes : []
      const work_notes = [
        ...existingNotes,
        {
          note: params.paid ? 'Payment completed' : 'Payment marked as pending',
          timestamp: now,
          user_id: userId,
        },
      ]

      await updateDoc(docRef, {
        paid: params.paid,
        paid_date: params.paid ? now : null,
        work_notes,
      })
      return params.repairTruckId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair_trucks'] })
      showSuccess('Paid status updated')
    },
    onError: (error: Error) => {
      showError(`Failed to update paid status: ${error.message}`)
    },
  })

  const updateRepairTruckFlaggedMutation = useMutation({
    mutationFn: async (params: { repairTruckId: string; flagged: boolean }) => {
      const docRef = doc(db, REPAIR_TRUCKS_COLLECTION, params.repairTruckId)
      const snap = await getDoc(docRef)
      if (!snap.exists()) throw new Error('Repair truck not found')
      const data = snap.data()
      const currentFlagged = Boolean(data?.flagged)
      if (currentFlagged === params.flagged) return params.repairTruckId

      await updateDoc(docRef, {
        flagged: params.flagged,
      })
      return params.repairTruckId
    },
    onSuccess: (_id, params) => {
      queryClient.invalidateQueries({ queryKey: ['repair_trucks'] })
      showSuccess(params.flagged ? 'Truck flagged for attention' : 'Truck flag removed')
    },
    onError: (error: Error) => {
      showError(`Failed to update flag: ${error.message}`)
    },
  })

  const updateRepairTruckPrioritiesMutation = useMutation({
    mutationFn: async (params: {
      repairShopLocationId: string
      orderedRepairTruckIds: string[]
    }) => {
      const { repairShopLocationId, orderedRepairTruckIds } = params
      const batch = writeBatch(db)
      orderedRepairTruckIds.forEach((repairTruckId, index) => {
        batch.update(doc(db, REPAIR_TRUCKS_COLLECTION, repairTruckId), {
          priority_rank: index + 1,
        })
      })
      if (orderedRepairTruckIds.length > 0) {
        await batch.commit()
      }
      return repairShopLocationId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair_trucks'] })
      showSuccess('Repair priorities updated')
    },
    onError: (error: Error) => {
      showError(`Failed to update repair priorities: ${error.message}`)
    },
  })

  const updateRepairTruckBeingPaintedMutation = useMutation({
    mutationFn: async (params: { repairTruckId: string; being_painted: boolean }) => {
      const repairDocRef = doc(db, REPAIR_TRUCKS_COLLECTION, params.repairTruckId)
      const repairSnap = await getDoc(repairDocRef)
      if (!repairSnap.exists()) throw new Error('Repair truck not found')
      const repairData = repairSnap.data()
      const currentBeingPainted = Boolean(repairData?.being_painted)
      if (currentBeingPainted === params.being_painted) return params.repairTruckId

      const now = Timestamp.now()
      const userId = userInfo?.id ?? ''
      const existingNotes = Array.isArray(repairData?.work_notes) ? repairData.work_notes : []
      const work_notes = [
        ...existingNotes,
        {
          note: params.being_painted ? 'Being painted' : 'Not being painted',
          timestamp: now,
          user_id: userId,
        },
      ]

      await updateDoc(repairDocRef, {
        being_painted: params.being_painted,
        being_painted_date: params.being_painted ? now : null,
        work_notes,
      })

      if (params.being_painted) {
        const truckId = typeof repairData?.transport_truck_id === 'string' ? repairData.transport_truck_id : ''
        if (truckId) {
          const truckSnap = await getDoc(doc(db, TRANSPORT_TRUCKS_COLLECTION, truckId))
          const truckData = truckSnap.data()
          const vehicleId = typeof truckData?.vehicle_id === 'string' ? truckData.vehicle_id : ''
          if (vehicleId) {
            const vehicleDocRef = doc(db, 'master_inventory', vehicleId)
            const vehicleSnap = await getDoc(vehicleDocRef)
            if (vehicleSnap.exists()) {
              const vehicleData = vehicleSnap.data()
              const previousColor = vehicleData?.color
              const colorHistory = Array.isArray(vehicleData?.color_history)
                ? [...vehicleData.color_history]
                : []
              if (previousColor !== 'Being painted') {
                colorHistory.push({
                  color: 'Being painted',
                  changed_at: now.toDate().toISOString(),
                  ...(userId && { changed_by_user_id: userId }),
                })
              }
              await updateDoc(vehicleDocRef, {
                color: 'Being painted',
                being_painted_date: now.toDate().toISOString(),
                color_history: colorHistory,
                updated_at: now.toDate().toISOString(),
                updated_by: userId || null,
              })
            }
          }
        }
      }

      return params.repairTruckId
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair_trucks'] })
      queryClient.invalidateQueries({ queryKey: ['transport_truck_vehicles'] })
      showSuccess('Paint status updated')
    },
    onError: (error: Error) => {
      showError(`Failed to update paint status: ${error.message}`)
    },
  })

  return {
    transportTrucks,
    transportTrucksWithVehicles,
    repairShopTruckCountsById,
    repairShopAvgDropoffToCompleteMsById,
    transporterGroupAvgReadyToInProgressMsById,
    isLoadingTransportTrucks: isLoadingTransportTrucks || isLoadingWithVehicles,
    isLoadingRepairTrucks,
    addTransportTruck: addTransportTruckMutation.mutateAsync,
    isAddingTransportTruck: addTransportTruckMutation.isPending,
    createShipmentPlan: createShipmentPlanMutation.mutateAsync,
    isCreatingShipmentPlan: createShipmentPlanMutation.isPending,
    updateShipmentPlan: updateShipmentPlanMutation.mutateAsync,
    isUpdatingShipmentPlan: updateShipmentPlanMutation.isPending,
    approveLegPhotos: approveLegPhotosMutation.mutateAsync,
    isApprovingLegPhotos: approveLegPhotosMutation.isPending,
    rejectLegPhotos: rejectLegPhotosMutation.mutateAsync,
    isRejectingLegPhotos: rejectLegPhotosMutation.isPending,
    updateTransportTruck: updateTransportTruckMutation.mutateAsync,
    isUpdatingTransportTruck: updateTransportTruckMutation.isPending,
    revertLatestTransportTruckStatus: revertLatestTransportTruckStatusMutation.mutateAsync,
    isRevertingLatestTransportTruckStatus: revertLatestTransportTruckStatusMutation.isPending,
    deleteShipmentPlan: deleteShipmentPlanMutation.mutateAsync,
    isDeletingShipmentPlan: deleteShipmentPlanMutation.isPending,
    deleteTransportTruck: deleteTransportTruckMutation.mutateAsync,
    isDeletingTransportTruck: deleteTransportTruckMutation.isPending,
    updateTransportTruckNeeds: updateTransportTruckNeedsMutation.mutateAsync,
    isUpdatingTransportTruckNeeds: updateTransportTruckNeedsMutation.isPending,
    addRepairTruckNote: addRepairTruckNoteMutation.mutateAsync,
    isAddingRepairTruckNote: addRepairTruckNoteMutation.isPending,
    updateRepairTruckNote: updateRepairTruckNoteMutation.mutateAsync,
    isUpdatingRepairTruckNote: updateRepairTruckNoteMutation.isPending,
    deleteRepairTruckNote: deleteRepairTruckNoteMutation.mutateAsync,
    isDeletingRepairTruckNote: deleteRepairTruckNoteMutation.isPending,
    updateRepairTruckWorkStatus: updateRepairTruckWorkStatusMutation.mutateAsync,
    isUpdatingRepairTruckWorkStatus: updateRepairTruckWorkStatusMutation.isPending,
    reviewRepairTruckEstimate: reviewRepairTruckEstimateMutation.mutateAsync,
    isReviewingRepairTruckEstimate: reviewRepairTruckEstimateMutation.isPending,
    updateRepairTruckPaid: updateRepairTruckPaidMutation.mutateAsync,
    isUpdatingRepairTruckPaid: updateRepairTruckPaidMutation.isPending,
    updateRepairTruckFlagged: updateRepairTruckFlaggedMutation.mutateAsync,
    isUpdatingRepairTruckFlagged: updateRepairTruckFlaggedMutation.isPending,
    updateRepairTruckPriorities: updateRepairTruckPrioritiesMutation.mutateAsync,
    isUpdatingRepairTruckPriorities: updateRepairTruckPrioritiesMutation.isPending,
    updateRepairTruckBeingPainted: updateRepairTruckBeingPaintedMutation.mutateAsync,
    isUpdatingRepairTruckBeingPainted: updateRepairTruckBeingPaintedMutation.isPending,
  }
}

const REPAIR_TRUCKS_CHUNK_SIZE = 30

/** Map key: `${transport_truck_id}_${repair_shop_location_id}` */
export function useRepairTrucksForTrucks(truckIds: string[]) {
  const repairTrucksCollection = collection(db, REPAIR_TRUCKS_COLLECTION).withConverter(
    repairTruckConverter
  )

  const { data: repairTrucksMap = new Map<string, RepairTruck>() } = useQuery({
    queryKey: ['repair_trucks', truckIds.length ? [...truckIds].sort().join(',') : ''],
    queryFn: async () => {
      if (truckIds.length === 0) return new Map<string, RepairTruck>()
      const map = new Map<string, RepairTruck>()
      for (let i = 0; i < truckIds.length; i += REPAIR_TRUCKS_CHUNK_SIZE) {
        const chunk = truckIds.slice(i, i + REPAIR_TRUCKS_CHUNK_SIZE)
        const q = query(
          repairTrucksCollection,
          where('transport_truck_id', 'in', chunk)
        )
        const snapshot = await getDocs(q)
        snapshot.docs.forEach((d) => {
          const r = d.data()
          const key = `${r.transport_truck_id}_${r.repair_shop_location_id}`
          const existing = map.get(key)
          const createdMs = r.created_at?.toMillis?.() ?? 0
          const existingCreatedMs = existing?.created_at?.toMillis?.() ?? 0
          if (!existing || createdMs >= existingCreatedMs) {
            map.set(key, r)
          }
        })
      }
      return map
    },
    enabled: truckIds.length > 0,
  })

  return repairTrucksMap
}

export function useAllRepairTrucks() {
  const repairTrucksCollection = collection(db, REPAIR_TRUCKS_COLLECTION).withConverter(
    repairTruckConverter
  )

  const { data: repairTrucks = [] } = useQuery({
    queryKey: ['repair_trucks', 'all'],
    queryFn: async () => {
      const snapshot = await getDocs(repairTrucksCollection)
      return snapshot.docs.map((d) => d.data())
    },
  })

  return repairTrucks
}

/** All repair_trucks documents for a single repair shop location. */
export function useRepairTrucksForShop(
  shopId: string | undefined,
  options?: { enabled?: boolean },
) {
  const repairTrucksCollection = collection(db, REPAIR_TRUCKS_COLLECTION).withConverter(
    repairTruckConverter
  )
  const enabled = (options?.enabled ?? true) && Boolean(shopId)

  const { data: repairTrucks = [], isLoading } = useQuery({
    queryKey: ['repair_trucks', 'shop', shopId],
    queryFn: async () => {
      const q = query(
        repairTrucksCollection,
        where('repair_shop_location_id', '==', shopId!),
      )
      const snapshot = await getDocs(q)
      return snapshot.docs.map((d) => d.data())
    },
    enabled,
  })

  return { repairTrucks, isLoading }
}
